import { createReducer, on } from '@ngrx/store';
import { TimesheetActions } from './timesheet.actions';
import { Timesheet, Note, DailyHours } from '@core/interface/timesheet';
import { Pagination } from '@core/interface/core';

export const TimesheetFeatureKey = 'timesheet';

export interface TimesheetState {
  timesheets: Timesheet[];
  isLoading: boolean;
  isAuditLoading: boolean;
  isDeleted: boolean;
  loadError?: any;
  deleteError?: any;
  addEditError?: any;
  pagination: Pagination;
  approved?: boolean;
  reopened?: boolean;
  notes: Note[];
  isClosed: number | null;
  shadow_engineer_name: string | null;
  is_shadow_engineer: number | null;
  hourStatus: {
    regular: string;
    overtime: string;
    total: string;
    lunchBreak: string;
    overtime1_5x: string;
    overtime2x: string;
    total_claim_hours: string;
  } | null;
  dailyHours: DailyHours | null;
  timesheetReport: {
    data: Timesheet[];
    isLoading: boolean;
    pagination: Pagination;
    isExcelLoading: boolean;
    isClosed: number | null;
    shadow_engineer_name: string | null;
    is_shadow_engineer: number | null;
    hourStatus: {
      regular: string;
      overtime: string;
      total: string;
      lunchBreak: string;
      overtime1_5x: string;
      overtime2x: string;
      total_claim_hours: string;
    } | null;
  };
  isUnsubmtted?: boolean;
  isCMChanged?: boolean;
  overtimeUpdated?: boolean;
}

export const initialState: TimesheetState = {
  timesheets: [],
  isLoading: false,
  isAuditLoading: false,
  isDeleted: false,
  notes: [],
  pagination: {
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  },
  hourStatus: null,
  dailyHours: null,
  isClosed: null,
  is_shadow_engineer: null,
  shadow_engineer_name: null,
  timesheetReport: {
    data: [],
    isLoading: false,
    pagination: {
      current_page: 1,
      last_page: 1,
      per_page: 10,
      total: 0,
    },
    hourStatus: null,
    isClosed: null,
    is_shadow_engineer: null,
    shadow_engineer_name: null,
    isExcelLoading: false,
  },
  isUnsubmtted: false,
  isCMChanged: false,
  overtimeUpdated: false,
};

export const TimesheetReducer = createReducer(
  initialState,
  on(TimesheetActions.loadTimesheets, (state) => {
    return {
      ...state,
      isLoading: true,
      timesheets: [],
    };
  }),
  on(TimesheetActions.loadTimesheetsSuccess, (state, data) => {
    return {
      ...state,
      pagination: data.pagination,
      timesheets: data.data,
      isLoading: false,
      isDeleted: false,
      loadError: null,
      isClosed: data.is_closed,
      shadow_engineer_name: data.shadow_engineer_name,
      is_shadow_engineer: data.is_shadow_engineer,
      hourStatus: {
        regular: data.regular_time ?? '0',
        overtime1_5x: data.total_claim_ot_1_5_hours ?? '0',
        overtime2x: data.total_claim_ot_2_0_hours ?? '0',
        overtime: data.overtime ?? '0',
        total: data.total_hours ?? '0',
        lunchBreak: data.lunch_break ?? '0',
        total_claim_hours: data.total_claim_hours ?? '0',
      },
      dailyHours: data.daily_hours ?? null,
      isUnsubmtted: false,
    };
  }),
  on(TimesheetActions.loadTimesheetsFailure, (state, { error }) => {
    return {
      ...state,
      loadError: error,
      isLoading: false,
      isDeleted: false,
      isClosed: null,
      hourStatus: null,
      dailyHours: null,
      is_shadow_engineer: null,
      shadow_engineer_name: null,
    };
  }),
  on(TimesheetActions.addTimesheetSuccess, (state, { data }) => ({
    ...state,
    timesheets: [...state.timesheets, data],
    isDeleted: false,
    addEditError: null,
  })),
  on(TimesheetActions.addTimesheetFailure, (state, error) => ({
    ...state,
    isDeleted: false,
    addEditError: error,
  })),
  on(TimesheetActions.updateTimesheetSuccess, (state, { data }) => ({
    ...state,
    timesheets: state.timesheets.map((Timesheet) =>
      data.id == Timesheet.id ? data : Timesheet
    ),
    isDeleted: false,
    addEditError: null,
  })),
  on(TimesheetActions.updateTimesheetFailure, (state, { error }) => ({
    ...state,
    isDeleted: false,
    addEditError: error,
  })),
  on(TimesheetActions.deleteTimesheetSuccess, (state, { success, id }) => ({
    ...state,
    isDeleted: success,
    deleteError: null,
    timesheets: state.timesheets.filter((Timesheet) => Timesheet.id !== id),
  })),
  on(TimesheetActions.deleteTimesheetFailure, (state, error) => ({
    ...state,
    isDeleted: false,
    deleteError: error,
  })),
  on(TimesheetActions.deleteTimesheet, (state, error) => ({
    ...state,
    isDeleted: false,
    deleteError: null,
  })),
  on(TimesheetActions.approveTimesheets, (state) => ({
    ...state,
    approved: false,
  })),
  on(TimesheetActions.approveTimesheetsSuccess, (state, data) => ({
    ...state,
    isDeleted: false,
    approvalError: null,
    approved: data.success,
  })),
  on(TimesheetActions.approveTimesheetsFailure, (state, { error }) => {
    return {
      ...state,
      approvalError: error,
      isDeleted: false,
      approved: false,
    };
  }),
  on(TimesheetActions.addAuditNote, (state) => ({
    ...state,
    isAuditLoading: true,
  })),

  on(TimesheetActions.addAuditSuccess, (state, { data }) => ({
    ...state,
    notes: [...state.notes, data],
    isDeleted: false,
    addEditError: null,
    isAuditLoading: false,
  })),
  on(TimesheetActions.addAuditFailure, (state, error) => ({
    ...state,
    isDeleted: false,
    addNoteError: error,
    isAuditLoading: false,
  })),

  on(TimesheetActions.loadAuditNotes, (state) => {
    return {
      ...state,
      isLoading: true,
    };
  }),
  on(TimesheetActions.loadAuditNotesSuccess, (state, { data }) => {
    return {
      ...state,
      isLoading: false,
      isDeleted: false,
      loadError: null,
      notes: data,
    };
  }),
  on(TimesheetActions.loadAuditNotesFailure, (state, { error }) => {
    return {
      ...state,
      loadAuditError: error,
      isLoading: false,
      isDeleted: false,
    };
  }),
  on(TimesheetActions.resetTimesheetParamsStatus, (state) => {
    return {
      ...state,
      approved: false,
      reopened: false,
      isCMChanged: false,
      overtimeUpdated: false,
    };
  }),
  on(TimesheetActions.resetTimesheets, (state) => {
    return {
      ...state,
      isLoading: false,
      timesheets: [],
      reopened: false,
      hourStatus: null,
      dailyHours: null,
    };
  }),
  on(TimesheetActions.reopenTimesheets, (state) => ({
    ...state,
    reopened: false,
  })),
  on(TimesheetActions.reopenTimesheetsSuccess, (state, data) => ({
    ...state,
    isDeleted: false,
    reopenError: null,
    reopened: data.success,
  })),
  on(TimesheetActions.reopenTimesheetsFailure, (state, { error }) => {
    return {
      ...state,
      reopenError: error,
      isDeleted: false,
      reopened: false,
    };
  }),

  on(TimesheetActions.loadTimesheetReport, (state) => {
    return {
      ...state,
      timesheetReport: {
        ...state.timesheetReport,
        data: [],
        isLoading: true,
      },
    };
  }),
  on(TimesheetActions.loadTimesheetReportSuccess, (state, data) => {
    return {
      ...state,
      timesheetReport: {
        ...state.timesheetReport,
        data: data.data,
        pagination: data.pagination,
        isLoading: false,
        isClosed: data.is_closed,
        shadow_engineer_name: data.shadow_engineer_name,
        is_shadow_engineer: data.is_shadow_engineer,
        hourStatus: {
          regular: data.regular_time ?? '0',
          overtime1_5x: data.total_claim_ot_1_5_hours ?? '0',
          overtime2x: data.total_claim_ot_2_0_hours ?? '0',
          overtime: data.overtime ?? '0',
          total: data.total_hours ?? '0',
          lunchBreak: data.lunch_break ?? '0',
          total_claim_hours: data.total_claim_hours ?? '0',
        },
      },
    };
  }),
  on(TimesheetActions.loadTimesheetReportFailure, (state, { error }) => {
    return {
      ...state,
      timesheetReport: {
        ...state.timesheetReport,
        isLoading: false,
        isClosed: null,
        hourStatus: null,
        is_shadow_engineer: null,
        shadow_engineer_name: null,
        data: [],
      },
    };
  }),
  on(TimesheetActions.resetTimesheetReport, (state) => {
    return {
      ...state,
      timesheetReport: {
        ...state.timesheetReport,
        isLoading: false,
        data: [],
        reopened: false,
        hourStatus: null,
        is_shadow_engineer: null,
        shadow_engineer_name: null,
      },
    };
  }),
  on(TimesheetActions.resetDeleteStatus, (state) => ({
    ...state,
    isDeleted: false,
  })),
  on(TimesheetActions.unSubmitTimesheet, (state) => ({
    ...state,
    isUnsubmtted: false,
  })),
  on(TimesheetActions.unSubmitTimesheetSuccess, (state) => ({
    ...state,
    isUnsubmtted: true,
  })),
  on(TimesheetActions.unSubmitTimesheetFailure, (state, { error }) => ({
    ...state,
    isUnsubmtted: false,
    unSubmitError: error,
  })),
  on(TimesheetActions.changeCMSuccess, (state) => ({
    ...state,
    isCMChanged: true,
  })),
  on(TimesheetActions.changeCMFailure, (state) => ({
    ...state,
    isCMChanged: false,
  })),
  on(TimesheetActions.storeManualOvertimeSuccess, (state) => ({
    ...state,
    overtimeUpdated: true,
  })),
  on(TimesheetActions.storeManualOvertimeFailure, (state) => ({
    ...state,
    overtimeUpdated: false,
  })),
  on(TimesheetActions.adminDirectApprove, (state) => ({
    ...state,
    approved: false,
  })),
  on(TimesheetActions.adminDirectApproveSuccess, (state, data) => ({
    ...state,
    approved: data.success,
  })),
  on(TimesheetActions.adminDirectApproveFailure, (state, { error }) => ({
    ...state,
    approved: false,
    approvalError: error,
  })),
  on(TimesheetActions.exportTimesheetReportExcel, (state) => ({
    ...state,
    timesheetReport: {
      ...state.timesheetReport,
      isExcelLoading: true,
    },
  })),
  on(TimesheetActions.exportTimesheetReportExcelSuccess, (state) => ({
    ...state,
    timesheetReport: {
      ...state.timesheetReport,
      isExcelLoading: false,
    },
  })),
  on(TimesheetActions.exportTimesheetReportExcelFailure, (state) => ({
    ...state,
    isExcelLoading: false,
    timesheetReport: {
      ...state.timesheetReport,
      isExcelLoading: false,
    },
  }))
);
